import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Immersive } from '@/types';
import type { CatalogType } from '../services/catalog/catalog-types';
import { firstMediaData } from '@/lib/utils/media.utils';
import { processTagArray, getYearTag, truncateToWords, combineTags } from '@/lib/utils/tagGenerator';

/**
 * Transformer for converting Immersive entities to CatalogItem
 */
export class ImmersiveTransformer extends BaseCatalogItemTransformer<Immersive> {
  protected getId(immersive: Immersive): string | number {
    return immersive.id;
  }

  protected getType(_immersive: Immersive): CatalogType {
    return 'immersive';
  }

  protected getSlug(immersive: Immersive): string {
    return immersive.slug;
  }

  protected getHref(immersive: Immersive): string {
    // Immersives are displayed under installations
    return `/catalog/immersive/${immersive.slug}`;
  }

  protected getTitle(immersive: Immersive): string {
    return immersive.title;
  }

  protected getDisplayTitle(immersive: Immersive): string {
    return immersive.title;
  }

  protected getFilename(immersive: Immersive): string {
    return immersive.slug;
  }

  protected getThumbnail(immersive: Immersive): string {
    return firstMediaData(immersive.cardImage, immersive.coverImage, immersive.meta?.image).url || '';
  }

  protected getImageUrl(immersive: Immersive): string {
    return firstMediaData(immersive.coverImage, immersive.cardImage, immersive.meta?.image).url || '';
  }

  protected getWidth(immersive: Immersive): number {
    return firstMediaData(immersive.cardImage, immersive.coverImage, immersive.meta?.image).width || 800;
  }

  protected getHeight(immersive: Immersive): number {
    return firstMediaData(immersive.cardImage, immersive.coverImage, immersive.meta?.image).height || 600;
  }

  protected getDate(immersive: Immersive): string {
    return immersive.date || immersive.createdAt;
  }

  protected getYear(immersive: Immersive): number {
    const date = immersive.date || immersive.createdAt;
    return new Date(date).getFullYear();
  }

  protected getDescription(immersive: Immersive): string {
    return immersive.description;
  }

  protected getLongDescription(immersive: Immersive): string {
    return immersive.description;
  }

  protected getDuration(immersive: Immersive): string | undefined {
    return immersive.duration || undefined;
  }

  protected getVideoUrl(immersive: Immersive): string | undefined {
    return immersive.experienceUrl || undefined;
  }

  protected getTags(immersive: Immersive): string[] {
    return combineTags(
      processTagArray(immersive.tags),
      getYearTag(immersive.date, immersive.createdAt),
      truncateToWords(immersive.platform, 3)
    );
  }
}

// Export singleton instance
export const immersiveTransformer = new ImmersiveTransformer();